import { useForm, FormProvider } from "react-hook-form";

import { Dialog } from "ui/dialog";
import {
  CreativeSelectionStep,
  CreativeSelectionStepActions,
} from "components/modals/add-creatives-with-writer-modal/steps/creative-selection-step/creative-selection-step";
import {
  AdditionalInstructionsStep,
  AdditionalInstructionsStepActions,
} from "components/modals/add-creatives-with-writer-modal/steps/additional-instructions-step/additional-instructions-step";

import {
  AddCreativesWithWriterContextProvider,
  useAddCreativesWithWriterContext,
} from "components/modals/add-creatives-with-writer-modal/context/add-creatives-with-writer-context";
import { useCampaignWriterCreatives } from "components/modals/add-creatives-with-writer-modal/hooks/use-campaign-writer-creatives";

import type {
  AddCreativesWithWriterFormData,
  AddCreativesWithWriterModalProps,
} from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.types";
import { TOTAL_ADD_CREATIVES_WITH_WRITER_STEPS } from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.constants";

export const AddCreativesWithWriterModal = ({
  setIsOpen = () => {},
  isOpen,
  folderId,
}: AddCreativesWithWriterModalProps) => {
  const methods = useForm<AddCreativesWithWriterFormData>({
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      selectedCreativeIds: [],
      creativeTemplates: {},
      additionalInstructions: "",
    },
  });

  if (!isOpen) {
    return null;
  }

  return (
    <AddCreativesWithWriterContextProvider isOpen={isOpen} setIsOpen={setIsOpen} folderId={folderId}>
      <FormProvider {...methods}>
        <AddCreativesWithWriterModalContent />
      </FormProvider>
    </AddCreativesWithWriterContextProvider>
  );
};

const AddCreativesWithWriterModalContent = () => {
  const { step, setIsOpen } = useAddCreativesWithWriterContext();
  const { creatives, isLoading, isError } = useCampaignWriterCreatives();

  const renderStep = () => {
    switch (step) {
      case 1:
        return <CreativeSelectionStep creatives={creatives} isLoading={isLoading} isError={isError} />;
      case 2:
        return <AdditionalInstructionsStep />;
      default:
        return <CreativeSelectionStep creatives={creatives} isLoading={isLoading} isError={isError} />;
    }
  };

  const renderActions = () => {
    switch (step) {
      case 1:
        return <CreativeSelectionStepActions isError={isError} />;
      case 2:
        return <AdditionalInstructionsStepActions />;
      default:
        return <CreativeSelectionStepActions isError={isError} />;
    }
  };

  return (
    <Dialog
      open
      size={step === 1 ? "xlarge" : "medium"}
      onClose={() => setIsOpen(false)}
      headline="Create with writer"
      step={{ current: step, total: TOTAL_ADD_CREATIVES_WITH_WRITER_STEPS }}
      actions={renderActions()}>
      <form className="flex flex-1 flex-col">{renderStep()}</form>
    </Dialog>
  );
};
